import React, { useEffect, useRef, useState } from 'react'
import { z } from 'zod'

const currentYearTwoDigits = Number(new Date().getFullYear().toString().slice(-2))
const currentYearLabel = currentYearTwoDigits.toString().padStart(2, '0')

export const creditCardSchema = z.object({
  cardNumber: z
    .string({ required_error: 'Informe o numero do cartao' })
    .trim()
    .min(1, 'Informe o numero do cartao')
    .regex(/^\d{16}$/, 'O numero do cartao deve conter apenas digitos e ter 16 digitos'),
  holderName: z
    .string({ required_error: 'Informe o nome do titular' })
    .trim()
    .min(3, 'O nome do titular deve ter ao menos 3 caracteres')
    .max(100, 'O nome do titular deve ter no maximo 100 caracteres'),
  expiryMonth: z
    .string({ required_error: 'Informe o mes' })
    .trim()
    .min(1, 'Informe o mes')
    .regex(/^(0[1-9]|1[0-2])$/, 'O mes deve estar entre 01 e 12'),
  expiryYear: z
    .string({ required_error: 'Informe o ano' })
    .trim()
    .regex(/^\d{2}$/, 'O ano deve conter 2 digitos')
    .refine(value => parseInt(value, 10) >= currentYearTwoDigits, `O ano deve ser maior ou igual a ${currentYearLabel}`),
  securityCode: z
    .string({ required_error: 'Informe o codigo' })
    .trim()
    .min(1, 'Informe o codigo')
    .regex(/^\d{3,4}$/, 'O codigo deve ter 3 ou 4 digitos')
})

export type CreditCardInfo = z.infer<typeof creditCardSchema>

export interface CreditCardModalProps {
  open: boolean
  onClose: () => void
  onPay: (data: CreditCardInfo) => Promise<void> | void
}

const initialState: CreditCardInfo = {
  cardNumber: '',
  holderName: '',
  expiryMonth: '',
  expiryYear: '',
  securityCode: ''
}


const CreditCardModal: React.FC<CreditCardModalProps> = ({ open, onClose, onPay }) => {
  const [form, setForm] = useState<CreditCardInfo>(initialState)
  const [errors, setErrors] = useState<Partial<Record<keyof CreditCardInfo, string>>>({})
  const [loading, setLoading] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)
  const firstInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (open) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.classList.add('modal-open')
      setTimeout(() => firstInput.current?.focus(), 0)
      modalRef.current?.focus()
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.classList.remove('modal-open')
    }
  }, [open, onClose])

  const handleChange = (
    field: keyof CreditCardInfo,
    transform?: (value: string) => string
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value
    const nextValue = transform ? transform(rawValue) : rawValue
    setForm(prev => ({ ...prev, [field]: nextValue }))
    setErrors(prev => {
      if (!prev[field]) return prev
      const { [field]: _removed, ...rest } = prev
      return rest
    })
  }



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const result = creditCardSchema.safeParse(form)
    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors
      const formattedErrors = Object.entries(fieldErrors).reduce((acc, [key, messages]) => {
        if (messages && messages[0]) {
          acc[key as keyof CreditCardInfo] = messages[0]
        }
        return acc
      }, {} as Partial<Record<keyof CreditCardInfo, string>>)
      setErrors(formattedErrors)
      return
    }

    setErrors({})
    try {
      setLoading(true)
      await onPay(result.data)
      setForm(initialState)
      onClose()
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setForm(initialState)
    setErrors({})
    onClose()
  }

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) handleCancel()
  }

  if (!open) return null

  const formattedNumber = form.cardNumber
    ? form.cardNumber.replace(/\D/g, '').padEnd(16, '•').replace(/(\d{4})(?=.)/g, '$1 ').trim()
    : '•••• •••• •••• ••••'

  return (
    <>
      <div className="modal-backdrop fade show" onClick={handleCancel} />
      <div
        className="modal fade show d-block"
        role="dialog"
        tabIndex={-1}
        ref={modalRef}
        onMouseDown={handleBackdrop}
        style={{ overflow: 'auto' }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document" onMouseDown={e => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header bg-primary text-white">
              <h5 className="modal-title fw-bold text-white">Cartão de crédito</h5>
              <button type="button" className="btn-close btn-close-white" aria-label="Fechar" onClick={handleCancel} />
            </div>
            <div className="modal-body">
              {/* Card Preview */}
              <div className="mb-4">
                <div
                  className="text-white position-relative overflow-hidden"
                  style={{
                    borderRadius: '0.875rem',
                    maxWidth: '100%',
                    margin: '0 auto',
                    padding: '1.25rem 1.5rem',
                    aspectRatio: '1.586',
                    maxHeight: 200,
                    background: 'linear-gradient(135deg, #e91e63 0%, #9c27b0 100%)',
                    boxShadow: '0 8px 24px rgba(233, 30, 99, 0.3)'
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div
                      style={{
                        width: 40,
                        height: 28,
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #ffd700, #ffb300)',
                        opacity: 0.9
                      }}
                    />
                    <img src="/assets/images/b-17.png" alt="bandeira" style={{ height: 24 }} />
                  </div>
                  <div className="h5 mb-3 font-monospace" style={{ letterSpacing: '0.15em', fontSize: '1.1rem' }}>
                    {formattedNumber}
                  </div>
                  <div className="d-flex justify-content-between align-items-end mt-auto">
                    <div>
                      <div className="text-uppercase" style={{ fontSize: '0.65rem', opacity: 0.7 }}>Titular</div>
                      <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                        {form.holderName ? form.holderName.toUpperCase() : 'SEU NOME AQUI'}
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="text-uppercase" style={{ fontSize: '0.65rem', opacity: 0.7 }}>Validade</div>
                      <div style={{ fontSize: '0.85rem' }}>
                        {form.expiryMonth || 'MM'}/{form.expiryYear || 'AA'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Número do cartão <span className="text-danger">*</span></label>
                  <input
                    ref={firstInput}
                    type="text"
                    inputMode="numeric"
                    maxLength={19}
                    placeholder="0000 0000 0000 0000"
                    className={`form-control ${errors.cardNumber ? 'is-invalid' : ''}`}
                    value={form.cardNumber
                      .replace(/\D/g, '')
                      .slice(0, 16)
                      .replace(/(.{4})/g, '$1 ')
                      .trim()
                    }
                    onChange={handleChange('cardNumber', value =>
                      value.replace(/\D/g, '').slice(0, 16)
                    )}
                  />
                  {errors.cardNumber && <div className="invalid-feedback">{errors.cardNumber}</div>}
                </div>

                <div className="mb-3">
                  <label className="form-label">Nome impresso no cartão <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    maxLength={100}
                    placeholder="Ex: JOÃO M SILVA"
                    className={`form-control ${errors.holderName ? 'is-invalid' : ''}`}
                    value={form.holderName}
                    onChange={handleChange('holderName')}
                  />
                  {errors.holderName && <div className="invalid-feedback">{errors.holderName}</div>}
                </div>

                <div className="row g-2">
                  <div className="col-4 mb-3">
                    <label className="form-label">Mês <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="MM"
                      maxLength={2}
                      className={`form-control ${errors.expiryMonth ? 'is-invalid' : ''}`}
                      value={form.expiryMonth}
                      onChange={e => {
                        let value = e.target.value.replace(/\D/g, '').slice(0, 2)
                        if (value) {
                          let num = parseInt(value, 10)
                          if (num < 0) num = 0
                          if (num > 12) num = 12
                          value = num.toString().padStart(value.length, '0')
                        }
                        setForm(prev => ({ ...prev, expiryMonth: value }))
                        setErrors(prev => {
                          if (!prev.expiryMonth) return prev
                          const { expiryMonth: _removed, ...rest } = prev
                          return rest
                        })
                      }}
                    />
                    {errors.expiryMonth && <div className="invalid-feedback">{errors.expiryMonth}</div>}
                  </div>
                  <div className="col-4 mb-3">
                    <label className="form-label">Ano <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="AA"
                      maxLength={2}
                      className={`form-control ${errors.expiryYear ? 'is-invalid' : ''}`}
                      value={form.expiryYear}
                      onChange={e => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 2)
                        setForm(prev => ({ ...prev, expiryYear: value }))
                        setErrors(prev => {
                          if (!prev.expiryYear) return prev
                          const { expiryYear: _removed, ...rest } = prev
                          return rest
                        })
                      }}
                    />
                    {errors.expiryYear && <div className="invalid-feedback">{errors.expiryYear}</div>}
                  </div>
                  <div className="col-4 mb-3">
                    <label className="form-label">CVV <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="000"
                      className={`form-control ${errors.securityCode ? 'is-invalid' : ''}`}
                      value={form.securityCode.replace(/\D/g, '').slice(0, 4)}
                      onChange={handleChange('securityCode', value => value.replace(/\D/g, '').slice(0, 4))}
                      maxLength={4}
                    />
                    {errors.securityCode && <div className="invalid-feedback">{errors.securityCode}</div>}
                  </div>
                </div>

                <div className="d-flex justify-content-between mt-4">
                  <button type="button" className="btn btn-outline-secondary" onClick={handleCancel} disabled={loading}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary px-4" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        Processando...
                      </>
                    ) : 'Pagar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CreditCardModal
